/**
 * @file UserGroups.tsx
 * @description This file contains the UserGroups component,
 * which utilizes the ExperimentTable component to work with User Groups (previouly Special Users) data.
 */

import React from "react";
import { RouteComponentProps } from "@reach/router";
import "./ExperimentTable.scss";
import { SpecialUser } from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import ExperimentTable from "./ExperimentTable";
import { EMPTY_CELL_TEXT, MAX_SHOWN_GROUPED_USER_EMAILS } from "./utils";
import { OverlayTrigger } from "../Components";
import { Tooltip } from "react-bootstrap";
import * as uuid from "uuid";
import { GroupedDataRow } from "./types";

const userColumnRenderer = (data: GroupedDataRow<SpecialUser, "user">) => {
  if (!data.user) {
    return <div className="experimentCellText">{EMPTY_CELL_TEXT}</div>;
  }

  // If the number of user emails is less than or equal to the max, just show the names comma separated
  if (data.user.length <= MAX_SHOWN_GROUPED_USER_EMAILS) {
    return <div className="experimentCellText">{data.user.join(", ")}</div>;
  }

  // Otherwise, show "<emails> + <remaining> more" with an overlay on hover
  // Show all user emails in the overlay
  const sortedUserEmails = [...data.user].sort();
  const shownUserEmails = sortedUserEmails.slice(0, MAX_SHOWN_GROUPED_USER_EMAILS);
  const remainingCount = sortedUserEmails.length - shownUserEmails.length;

  return (
    <OverlayTrigger
      placement={OverlayTrigger.PLACEMENTS.BOTTOM.CENTER}
      overlay={
        <Tooltip id={uuid.v4()}>
          <div>
            {sortedUserEmails.map((userEmail, idx) => (
              <div key={idx}>{userEmail}</div>
            ))}
          </div>
        </Tooltip>
      }
    >
      <div className="experimentCellText">
        {`${shownUserEmails.join(", ")}${remainingCount > 0 ? ` + ${remainingCount} more` : ""}`}
      </div>
    </OverlayTrigger>
  );
};

const headers = [
  {
    label: "User Group",
    field: "user_group",
    type: "text",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (userGroup: string) => {
      return !userGroup || userGroup.length === 0;
    },
  },
  {
    label: "User Emails",
    field: "user",
    type: "select",
    isMulti: true,
    options: "special_user",
    flex: 3,
    modalRow: 0,
    modalFlex: 1,
    isInvalid: (user: string) => {
      return !user || user.length === 0;
    },
    renderer: userColumnRenderer,
  },
];

const UserGroups: React.FC<RouteComponentProps> = ({ path }) => {
  // Factory function to create new CompanyExperiment rows
  const createNewSpecialUser = (groupByColumn: string, groupedColumn: string): SpecialUser => {
    return {
      user: groupedColumn,
      user_group: groupByColumn,
    };
  };

  return (
    <ExperimentTable<SpecialUser>
      options={{
        headers,
        name: "User Groups",
        addButtonText: "Add Group",
        createNewRow: createNewSpecialUser,
        groupByColumn: "user_group",
        groupedColumn: "user",
        expectedKeys: ["user_group", "user"],
        jsonField: "special_users",
        modalTitle: "New User Group",
      }}
    />
  );
};

export default UserGroups;
